import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Bell,
  BellRing,
  CheckCircle2,
  Download,
  ExternalLink,
  LogOut,
  Share2,
  Smartphone,
  TestTube2
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, ApiClientError, downloadUrl } from '../api';
import { Logo } from '../components/Logo';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';
import {
  canPromptInstall,
  isAndroid,
  isIos,
  isStandalone,
  promptInstall,
  subscribeToPush
} from '../pwa';

interface Preferences {
  dailyLogEnabled: boolean;
  dailyLogTime: string;
  taskEnabled: boolean;
  rewardEnabled: boolean;
  timezone: string;
}

interface PushConfig {
  enabled: boolean;
  publicKey: string;
}

export function InstallPage() {
  const { me, status, logout } = useAuth();
  const { show } = useToast();
  const navigate = useNavigate();
  const [platform, setPlatform] = useState<'ios' | 'android'>(() => isIos() ? 'ios' : 'android');
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [pushConfig, setPushConfig] = useState<PushConfig | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState('');
  const [installAvailable, setInstallAvailable] = useState(canPromptInstall());
  const standalone = isStandalone();

  useEffect(() => {
    const onAvailable = () => setInstallAvailable(canPromptInstall());
    window.addEventListener('kidee:install-available', onAvailable);
    window.addEventListener('kidee:installed', onAvailable);
    return () => {
      window.removeEventListener('kidee:install-available', onAvailable);
      window.removeEventListener('kidee:installed', onAvailable);
    };
  }, []);

  useEffect(() => {
    Promise.all([
      api<Preferences>('/notifications/preferences'),
      api<PushConfig>('/push/public-key'),
      navigator.serviceWorker?.ready
        .then((registration) => registration.pushManager.getSubscription())
        .catch(() => null)
    ])
      .then(([prefs, config, subscription]) => {
        setPreferences(prefs);
        setPushConfig(config);
        setSubscribed(Boolean(subscription));
      })
      .catch((error) => show(error instanceof ApiClientError ? error.message : 'Không thể tải cài đặt.', 'error'));
  }, [show]);

  const pushSupported = useMemo(
    () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window,
    []
  );

  async function install() {
    setBusy('install');
    try {
      const accepted = await promptInstall();
      show(accepted ? 'Kidee đã được thêm vào thiết bị.' : 'Bạn đã đóng lời mời cài đặt.', accepted ? 'success' : 'info');
    } finally {
      setBusy('');
    }
  }

  async function enablePush() {
    if (!pushConfig?.enabled || !pushConfig.publicKey) {
      show('Máy chủ chưa có VAPID keys. Xem README để cấu hình Web Push.', 'error');
      return;
    }
    if (!pushSupported) {
      show('Trình duyệt này chưa hỗ trợ Web Push.', 'error');
      return;
    }
    if (isIos() && !standalone) {
      show('Trên iPhone/iPad, hãy thêm Kidee vào Màn hình chính trước rồi mở app để bật thông báo.', 'info');
      return;
    }
    setBusy('push');
    try {
      const subscription = await subscribeToPush(pushConfig.publicKey);
      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
        throw new Error('Push subscription không đầy đủ.');
      }
      await api('/push/subscriptions', {
        method: 'POST',
        body: { endpoint: json.endpoint, keys: json.keys }
      });
      setSubscribed(true);
      show('Đã bật thông báo cho thiết bị này.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'Không thể bật thông báo.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function savePreferences(next = preferences) {
    if (!next) return;
    setBusy('preferences');
    try {
      const saved = await api<Preferences>('/notifications/preferences', { method: 'PUT', body: next });
      setPreferences(saved);
      show('Đã lưu lịch nhắc.', 'success');
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể lưu lịch nhắc.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function testPush() {
    setBusy('test');
    try {
      const result = await api<{ attempted: number; delivered: number }>('/push/test', { method: 'POST' });
      show(result.delivered > 0 ? 'Thông báo thử đã được gửi.' : 'Chưa có subscription hoạt động trên tài khoản này.', result.delivered > 0 ? 'success' : 'info');
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể gửi thông báo thử.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  if (!preferences || !pushConfig) return <Loading label="Đang kiểm tra thiết bị…" />;

  return (
    <div className="install-page page--full">
      <header className="survey-header">
        <Link to="/" className="round-back" aria-label="Quay lại"><ArrowLeft /></Link>
        <Logo compact />
        <span className="child-chip">{me?.user.displayName}</span>
      </header>

      <div className="install-intro">
        <div><span className="eyebrow"><Smartphone size={15} /> PWA & Web Push</span><h1>Cài Kidee trên điện thoại</h1><p>Dùng toàn màn hình như một app và nhận nhắc nhở đúng lúc.</p></div>
        <div className="mascot mascot--small" aria-hidden="true">🦕⭐</div>
      </div>

      <section className="install-status-strip">
        <span className={standalone ? 'ok' : ''}>{standalone ? <CheckCircle2 /> : <Download />}</span>
        <div><strong>{standalone ? 'Kidee đang chạy như một app' : 'Kidee chưa được cài trên Màn hình chính'}</strong><p>{standalone ? 'Bạn có thể bật thông báo ngay bên dưới.' : 'Cài đặt không cần App Store hay Google Play.'}</p></div>
        {!standalone && installAvailable && <button className="button button--primary button--small" type="button" disabled={busy === 'install'} onClick={() => void install()}>Cài ngay</button>}
      </section>

      <div className="platform-tabs">
        <button type="button" className={platform === 'ios' ? 'active' : ''} onClick={() => setPlatform('ios')}> iPhone</button>
        <button type="button" className={platform === 'android' ? 'active' : ''} onClick={() => setPlatform('android')}>🤖 Android</button>
      </div>

      <section className="surface install-steps">
        {platform === 'ios' ? (
          <>
            <h2>📱 Cài trên iPhone/iPad bằng Safari</h2>
            <div className="steps-grid steps-grid--three">
              <div><b>1</b><span><Share2 /></span><strong>Nhấn Chia sẻ</strong><p>Biểu tượng hình vuông có mũi tên lên.</p></div>
              <div><b>2</b><span>＋</span><strong>Thêm vào MH chính</strong><p>Cuộn menu và chọn mục này.</p></div>
              <div><b>3</b><span>🟪</span><strong>Nhấn Thêm</strong><p>Mở Kidee từ biểu tượng mới.</p></div>
            </div>
            <p className="platform-note">Safari yêu cầu mở web app từ Màn hình chính trước khi xin quyền Web Push.</p>
          </>
        ) : (
          <>
            <h2>🤖 Cài trên Android bằng Chrome</h2>
            <div className="steps-grid">
              <div><b>1</b><span>⋮</span><strong>Mở menu Chrome</strong><p>Nhấn dấu ba chấm ở góc trên.</p></div>
              <div><b>2</b><span><Download /></span><strong>Cài đặt ứng dụng</strong><p>Xác nhận để thêm Kidee vào màn hình.</p></div>
            </div>
            {installAvailable && <button type="button" className="button button--primary button--wide" onClick={() => void install()} disabled={busy === 'install'}><Download size={18} /> Cài Kidee</button>}
          </>
        )}
      </section>

      <section className="surface push-section">
        <div className="section-heading section-heading--stack">
          <div><span className="section-icon section-icon--yellow"><BellRing size={20} /></span><h2>Bật thông báo</h2></div>
          <p>Quyền chỉ được hỏi sau khi bạn chủ động nhấn nút.</p>
        </div>
        {!status?.pushConfigured && <div className="config-warning">Web Push chưa được cấu hình trên máy chủ (`VAPID_PUBLIC_KEY`/`VAPID_PRIVATE_KEY`).</div>}
        <div className="notification-list">
          <label>
            <span className="notification-icon notification-icon--purple">🗓️</span>
            <div><strong>Nhắc ghi điểm hằng ngày</strong><small>Nhắc phụ huynh ghi nhận việc tốt trong ngày.</small></div>
            <span className="switch"><input type="checkbox" checked={preferences.dailyLogEnabled} onChange={(event) => setPreferences({ ...preferences, dailyLogEnabled: event.target.checked })} /><span /></span>
          </label>
          <label>
            <span className="notification-icon notification-icon--green">✅</span>
            <div><strong>Nhắc nhiệm vụ</strong><small>Gửi theo ngày và giờ của từng nhiệm vụ.</small></div>
            <span className="switch"><input type="checkbox" checked={preferences.taskEnabled} onChange={(event) => setPreferences({ ...preferences, taskEnabled: event.target.checked })} /><span /></span>
          </label>
          <label>
            <span className="notification-icon notification-icon--orange">🎁</span>
            <div><strong>Có gợi ý quà mới</strong><small>Báo sau khi kết quả AI được kiểm tra và lưu.</small></div>
            <span className="switch"><input type="checkbox" checked={preferences.rewardEnabled} onChange={(event) => setPreferences({ ...preferences, rewardEnabled: event.target.checked })} /><span /></span>
          </label>
        </div>
        <div className="form-grid form-grid--compact">
          <label>Giờ nhắc ghi điểm<input type="time" value={preferences.dailyLogTime} onChange={(event) => setPreferences({ ...preferences, dailyLogTime: event.target.value })} /></label>
          <label>Múi giờ<input value={preferences.timezone} onChange={(event) => setPreferences({ ...preferences, timezone: event.target.value })} /></label>
        </div>
        <div className="button-row">
          <button className="button button--soft" type="button" onClick={() => void savePreferences()} disabled={busy === 'preferences'}>Lưu lịch nhắc</button>
          <button className="button button--primary" type="button" onClick={() => void enablePush()} disabled={busy === 'push' || subscribed}>{subscribed ? <CheckCircle2 size={18} /> : <Bell size={18} />}{subscribed ? 'Đã bật trên thiết bị' : 'Bật thông báo'}</button>
        </div>
        {subscribed && <button className="text-button" type="button" onClick={() => void testPush()} disabled={busy === 'test'}><TestTube2 size={16} /> Gửi thông báo thử</button>}
      </section>

      <section className="surface account-section">
        <h2>Dữ liệu & tài khoản</h2>
        <a className="account-row" href={downloadUrl('/admin/export')}><Download size={19} /><span><strong>Tải bản sao dữ liệu</strong><small>JSON gồm hồ sơ, điểm, task, catalog và lịch sử đổi quà.</small></span><ExternalLink size={16} /></a>
        <button className="account-row account-row--danger" type="button" onClick={() => void signOut()}><LogOut size={19} /><span><strong>Đăng xuất</strong><small>Xóa phiên trên trình duyệt này.</small></span></button>
      </section>
    </div>
  );
}
